<template>
  <b-modal
    v-if="offer"
    id="modal-offer-dynamic"
    modal-class="offer-dynamic-modal"
    dialog-class="offer-dynamic-dialog"
    content-class="offer-dynamic-content"
    body-class="offer-modal-body"
    :hide-header="true"
    :hide-footer="true"
    :no-close-on-backdrop="false"
    :no-fade="false"
    :backdrop-opacity="0.85"
    @hidden="onHidden"
    @shown="onShown"
  >
    <template slot="default">
      <div class="offer-container" :style="{ transform: `scale(${scale})` }">
        <img
          v-if="offer && offer.image_path_full"
          :src="offer.image_path_full"
          style="display: none"
          @load="bgFailed = false"
          @error="bgFailed = true"
        />

        <div class="offer-background" :style="offerBackgroundStyle"></div>

        <button class="offer-close-btn" @click="closeModal">
          <span>&times;</span>
        </button>

        <offer-layout-single
          v-if="offer.display_type === 'single' && firstPackage"
          :offer="offer"
          :first-package="firstPackage"
          :display-config="displayConfig"
          :formatted-description="formattedDescription"
          :description-style="descriptionStyle"
          @purchase="handlePurchase"
        />

        <offer-layout-double
          v-else-if="offer.display_type === 'double'"
          :offer="offer"
          :display-config="displayConfig"
          :formatted-description="formattedDescription"
          :description-style="descriptionStyle"
          @purchase="handlePurchase"
        />

        <offer-layout-triple
          v-else-if="offer.display_type === 'triple'"
          :offer="offer"
          :display-config="displayConfig"
          :formatted-description="formattedDescription"
          :description-style="descriptionStyle"
          @purchase="handlePurchase"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import OfferLayoutSingle from "./pay/offer-layout-single.vue";
import OfferLayoutDouble from "./pay/offer-layout-double.vue";
import OfferLayoutTriple from "./pay/offer-layout-triple.vue";
import AnimationFullRewardEarned from "@/components/animation/animation-full-reward-earned";
import { offerMethodsMixin } from "./pay/offerMethodsMixin";
import { freeClaim } from "./mixins/freeClaim";

export default {
  name: "ModalOfferDynamic",
  components: {
    OfferLayoutSingle,
    OfferLayoutDouble,
    OfferLayoutTriple,
  },
  mixins: [AnimationFullRewardEarned, offerMethodsMixin, freeClaim],
  props: {
    offerData: {
      type: Object,
      required: true,
      default: null,
    },
  },
  data() {
    return {
      scale: 1,
      bgFailed: false,
    };
  },
  computed: {
    offer() {
      return this.offerData;
    },
    firstPackage() {
      return this.offer && this.offer.packages && this.offer.packages.length
        ? this.offer.packages[0]
        : null;
    },
    displayConfig() {
      if (!this.offer || !this.offer.display_config) return null;

      try {
        return typeof this.offer.display_config === "string"
          ? JSON.parse(this.offer.display_config)
          : this.offer.display_config;
      } catch (e) {
        console.error("Error parsing display_config:", e);
        return null;
      }
    },
    formattedDescription() {
      if (!this.displayConfig || !this.displayConfig.description) return "";

      const locale = this.$store.state.locale || "de_DE";
      const description =
        this.displayConfig.description[locale] ||
        this.displayConfig.description.fallback ||
        "";

      return description
        .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
    },
    descriptionStyle() {
      if (!this.displayConfig || !this.displayConfig.style) return {};

      const style = this.displayConfig.style;
      const shadowColor = style["text-shadow-color"];

      return {
        color: style["text-color"] || "#fff",
        "--highlight-color": style["text-highlight-color"] || "#CBC3E3",
        "--text-shadow-color": shadowColor || "none",
      };
    },
    offerBackgroundStyle() {
      const url = this.offer && this.offer.image_path_full;
      const fallback = "linear-gradient(#444, #444)";

      return {
        backgroundImage: !url || this.bgFailed ? fallback : `url("${url}")`,
      };
    },
  },
  watch: {
    offer(newVal) {
      if (newVal) {
        this.calculateScale();
        this.openModal();
      }
    },
  },
  mounted() {
    this.calculateScale();

    if (this.offer) {
      this.openModal();
    }

    window.addEventListener("resize", this.calculateScale);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calculateScale);
  },
  methods: {
    openModal() {
      this.$nextTick(() => {
        this.$bvModal.show("modal-offer-dynamic");
      });
    },
    closeModal() {
      this.$bvModal.hide("modal-offer-dynamic");
    },
    onHidden() {
      this.flushPendingOfferClear();
      this.$emit("hidden");
    },
    onShown() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.fitDescriptionText();
        }, 50);
      });
    },
    calculateScale() {
      const modalWidth = 1704;
      const modalHeight = 786;
      const padding = 40;

      const availableWidth = window.innerWidth - padding;
      const availableHeight = window.innerHeight - 60;

      const scaleX = availableWidth / modalWidth;
      const scaleY = availableHeight / modalHeight;

      this.scale = Math.min(1, scaleX, scaleY);
    },
    fitDescriptionText() {
      const descriptions = document.querySelectorAll(
        "#modal-offer-dynamic .offer-description"
      );

      if (!descriptions || descriptions.length === 0) return;

      descriptions.forEach((element) => {
        const maxWidth = element.offsetWidth;
        const maxHeight = element.offsetHeight;

        if (!maxWidth || !maxHeight) return;

        let fontSize = 30;
        element.style.fontSize = fontSize + "px";

        while (
          (element.scrollHeight > maxHeight ||
            element.scrollWidth > maxWidth) &&
          fontSize > 14
        ) {
          fontSize -= 1;
          element.style.fontSize = fontSize + "px";
        }

        element.classList.add("visible");
      });
    },
    handlePurchase(payPackage) {
      if (
        !payPackage ||
        !payPackage.sku ||
        !payPackage.sku.prices ||
        !payPackage.sku.prices.length
      ) {
        console.error("Invalid package data:", {
          packageId: payPackage && payPackage.id,
          sku: payPackage && payPackage.sku,
          payPackage,
        });
        return;
      }
      if (this.isFreePackage(payPackage)) {
        this.playSoundTab();
        this.firebaseClickItemOffer(
          "offer_claim_button_" + payPackage.id + "#"
        );
        this.claimPayOfferPackage(payPackage, this.offer && this.offer.id);
        return;
      }

      this.playSoundTab();
      this.firebaseClickItemOffer("offer_buy_button_" + payPackage.id + "#");

      const locale = this.$store.state.locale || "de_DE";
      const displayConfig =
        typeof payPackage.display_config === "string"
          ? JSON.parse(payPackage.display_config)
          : payPackage.display_config;

      const localizedName =
        (displayConfig && displayConfig.name && displayConfig.name[locale]) ||
        (displayConfig && displayConfig.name && displayConfig.name.fallback) ||
        payPackage.name;

      const packageData = {
        id: payPackage.id,
        package: {
          id: payPackage.id,
          name: payPackage.name,
          slug: payPackage.slug || "dynamic-offer",
          price: payPackage.sku.prices.find((p) => p.platform === "web")?.price,
          items: payPackage.assets || [],
        },
        rewards: this.parseRewardsFromAssets(payPackage.assets),
        category: "Offer",
        variant: "Dynamic",
        customName: localizedName,
        customImg: payPackage.image_path_full,
        v2Package: payPackage,
        v2OfferId: this.offer.id,
      };

      this.$store.commit("shop/setShopChosenPackage", packageData);

      this.$bvModal.show("modal-payment");
    },
    parseRewardsFromAssets(assets) {
      const rewards = {
        gold: 0,
        crown: 0,
        loyalty: 0,
        elixir: 0,
        card: 0,
        expbooster: 0,
      };

      if (!assets || !Array.isArray(assets)) {
        return rewards;
      }

      assets.forEach((asset) => {
        let type = "";

        if (asset.asset_type || asset.assetType || asset.type) {
          type = (
            asset.asset_type ||
            asset.assetType ||
            asset.type
          ).toLowerCase();
        } else if (asset.asset) {
          const className = asset.asset;
          const lastBackslash = className.lastIndexOf("\\");
          type = (
            lastBackslash >= 0
              ? className.substring(lastBackslash + 1)
              : className
          ).toLowerCase();
        }

        const amount = parseInt(asset.amount, 10) || 0;

        switch (type) {
          case "gold":
            rewards.gold += amount;
            break;
          case "crown":
            rewards.crown += amount;
            break;
          case "loyalty":
            rewards.loyalty += amount;
            break;
          case "elixir":
            rewards.elixir += amount;
            break;
          case "expbooster":
            rewards.expbooster += amount;
            break;
          case "card":
            rewards.card += amount;
            break;
        }
      });

      return rewards;
    },
  },
};
</script>

<style lang="scss">
.offer-dynamic-modal {
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 20px !important;
  overflow: hidden;
}

.offer-dynamic-modal .modal-dialog.offer-dynamic-dialog {
  max-width: none;
  width: auto;
  margin: 0 !important;
  flex: 0 0 auto;
}

.offer-dynamic-modal .modal-content.offer-dynamic-content {
  background: transparent;
  border: none;
  box-shadow: none;
  width: auto;
}

.offer-dynamic-modal .modal-body.offer-modal-body {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}
</style>

<style scoped lang="scss">
.offer-container {
  position: relative;
  width: 1704px;
  height: 786px;
  overflow: hidden;
  transform-origin: center center;
  flex: 0 0 auto;
}

.offer-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.offer-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  span {
    color: white;
    font-size: 28px;
    line-height: 0;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -3px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    border-color: white;
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
  }
}
</style>
